import { useEffect, useState } from "react";
import { getLessonBrief, saveLessonNotes, type LessonBrief } from "../../lib/live-api";

interface Props { lessonId: string; }

export function LiveTutorPanel({ lessonId }: Props): JSX.Element {
  const [brief, setBrief] = useState<LessonBrief | null>(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [tab, setTab] = useState<"brief" | "notes">("brief");

  useEffect(() => {
    getLessonBrief(lessonId)
      .then((b) => setBrief(b))
      .finally(() => setLoading(false));
  }, [lessonId]);

  async function save(): Promise<void> {
    if (!notes.trim()) return;
    setSaving(true);
    setSaved(false);
    const ok = await saveLessonNotes(lessonId, notes);
    setSaving(false);
    if (ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  }

  if (loading) {
    return <div className="text-xs text-ink-400 px-3 py-2">Loading brief…</div>;
  }

  return (
    <div className="rounded-2xl border border-ink-700 bg-ink-900/80 text-sm overflow-hidden">
      <div className="flex border-b border-ink-700">
        <button
          onClick={() => setTab("brief")}
          className={`flex-1 px-3 py-2 text-xs uppercase tracking-widest ${tab === "brief" ? "bg-ink-800 text-white" : "text-ink-400 hover:text-white"}`}
        >
          {"📋"} Pre-lesson brief
        </button>
        <button
          onClick={() => setTab("notes")}
          className={`flex-1 px-3 py-2 text-xs uppercase tracking-widest ${tab === "notes" ? "bg-ink-800 text-white" : "text-ink-400 hover:text-white"}`}
        >
          {"📝"} Post-lesson notes
        </button>
      </div>

      <div className="p-4 max-h-[60vh] overflow-y-auto">
        {tab === "brief" && (brief ? (
          <div className="space-y-3 text-xs">
            <section>
              <div className="text-ink-400 uppercase tracking-widest text-[10px]">Learner</div>
              <div className="text-white font-medium mt-1">
                {brief.learner.name ?? "—"}
                <span className="text-ink-400 font-normal ml-2">
                  {brief.learner.level ?? "?"}
                  {brief.learner.target_level && ` → ${brief.learner.target_level}`}
                  {brief.learner.track && ` · ${brief.learner.track}`}
                </span>
              </div>
              {brief.learner.profession && (
                <div className="text-ink-300 mt-0.5">{brief.learner.profession}</div>
              )}
              {brief.learner.interests.length > 0 && (
                <div className="text-ink-400 mt-0.5">Interests: {brief.learner.interests.join(", ")}</div>
              )}
            </section>

            {brief.pep_talk && (
              <section className="rounded-lg border border-lime-700/40 bg-lime-500/5 p-3">
                <div className="text-[10px] uppercase tracking-widest text-lime-300/80">Opener</div>
                <div className="text-ink-100 mt-1 italic">"{brief.pep_talk}"</div>
              </section>
            )}

            {brief.suggested_topics.length > 0 && (
              <section>
                <div className="text-ink-400 uppercase tracking-widest text-[10px] mb-1">Suggested topics</div>
                <ul className="space-y-1">
                  {brief.suggested_topics.map((t, i) => (
                    <li key={i} className="text-ink-100">• {t}</li>
                  ))}
                </ul>
              </section>
            )}

            {brief.recent_summaries.length > 0 && (
              <section>
                <div className="text-ink-400 uppercase tracking-widest text-[10px] mb-1">Recent AI sessions</div>
                <ul className="space-y-2">
                  {brief.recent_summaries.map((r, i) => (
                    <li key={i} className="rounded border border-ink-700 bg-ink-800/60 p-2">
                      <div className="text-[10px] text-ink-500">{r.when ? new Date(r.when).toLocaleDateString() : ""}</div>
                      <div className="text-ink-100 mt-0.5">{r.narrative}</div>
                      {r.focus && <div className="text-ink-300 mt-0.5 italic">Focus: {r.focus}</div>}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {brief.recurring_errors.length > 0 && (
              <section>
                <div className="text-ink-400 uppercase tracking-widest text-[10px] mb-1">Recurring patterns</div>
                <ul className="space-y-1">
                  {brief.recurring_errors.map((e, i) => (
                    <li key={i} className="text-ink-100">
                      <span className="font-medium">{e.category.replace(/-/g, " ")}</span>
                      <span className="text-ink-500 font-mono ml-1">×{e.count}</span>
                      <span className="text-ink-400 ml-2">{e.explanation}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {brief.new_vocab_2w.length > 0 && (
              <section>
                <div className="text-ink-400 uppercase tracking-widest text-[10px] mb-1">New vocab (14d)</div>
                <div className="text-ink-200 leading-relaxed">{brief.new_vocab_2w.join(", ")}</div>
              </section>
            )}

            <section>
              <div className="text-ink-400 uppercase tracking-widest text-[10px] mb-1">Homework</div>
              <div className="text-ink-100">
                {brief.homework.completed} / {brief.homework.assigned} completed
              </div>
              {brief.homework.recent_prompts.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {brief.homework.recent_prompts.map((p, i) => (
                    <li key={i} className="text-ink-300">• {p}</li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : (
          <div className="text-ink-400">No brief available.</div>
        ))}

        {tab === "notes" && (
          <div className="space-y-3">
            <p className="text-xs text-ink-400">
              Your notes go into the learner's long-term memory — Arthur will reference them in future AI sessions.
              Use bullets or short lines, one observation per line.
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={10}
              placeholder={"- struggles with present perfect vs past simple\n- expand vocab around 'business meetings'\n- pronounces /θ/ as /s/ in 'think', 'three'"}
              className="w-full rounded-xl bg-ink-800 border border-ink-700 px-3 py-2 text-sm text-ink-100 placeholder-ink-500 focus:outline-none focus:border-lime-500"
            />
            <div className="flex items-center justify-between">
              {saved ? (
                <span className="text-emerald-300 text-xs">{"✓"} Saved + sent to AI memory.</span>
              ) : <span />}
              <button
                onClick={save}
                disabled={saving || !notes.trim()}
                className="rounded-xl bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold px-4 py-2 text-sm disabled:opacity-40"
              >
                {saving ? "Saving…" : "Save notes"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
